import {
  cleanLocations,
  isPlaceholderSubdivision,
  Range,
  SearchFormData,
} from "../store/searchStore";
import { PropertyFeature, PropertyType, Sort } from "../types/constants";

export interface PropertyResult {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  acres: number;
  image: string;
  propertyType: string;
  latitude: number | null;
  longitude: number | null;
  url: string;
  daysOnMarket: number;
  /** Listing brokerage (ListOfficeName), shown as the MLS attribution. Empty when the feed has none. */
  listOffice: string;
}

export interface SearchResponse {
  results: PropertyResult[];
  total: number;
  offset: number;
  limit: number;
}

/** One map pin: the whole result set is plotted from these, unpaged. */
export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  price: number;
}

export interface PinsResponse {
  pins: MapPin[];
  total: number;
}

/** Structured filters parsed from a natural-language query by ParseQuery. */
export interface ParsedFilters {
  beds: number;
  maxBeds: number | null;
  baths: number;
  maxBaths: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  minSqft: number | null;
  maxSqft: number | null;
  minAcres: number | null;
  maxAcres: number | null;
  minYearBuilt: number | null;
  maxYearBuilt: number | null;
  /** Semicolon-joined city list, matching the `location` query param. */
  location: string;
  address: string | null;
  subdivision: string | null;
  propertyTypes: string[];
  propertyFeatures: string[];
  sort: string;
  maxDaysOnMarket: number | null;
  keywords: string[];
}

export interface ServiceAreaHit {
  name: string;
  lat: number;
  lng: number;
  zoom: number;
}

export interface ParseQueryResponse {
  filters: ParsedFilters | null;
  summary: string;
  /** Resolved service area to fly the map to when nothing matches, or null. */
  area: ServiceAreaHit | null;
  /** False when the query named a place we don't serve → block the search. */
  inServiceArea: boolean;
  /** True when the per-IP rate limit was hit. */
  rateLimited?: boolean;
}

const API_BASE = "/umbraco/api/PropertySearch";

/**
 * Maps React PropertyType enum values to API query string values.
 */
function mapPropertyTypes(types: PropertyType[]): string {
  return types.join(",");
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Encode the user-facing filter state into URL params. Reused by the Search
 * and Pins calls and by buildShareUrl so a shared link contains exactly the
 * same fields the search would use.
 */
function buildSearchParams(formData: SearchFormData): URLSearchParams {
  const params = new URLSearchParams();

  if (formData.location.length > 0) params.set("location", formData.location.join(";"));
  if (formData.address) params.set("address", formData.address);
  if (formData.subdivision) params.set("subdivision", formData.subdivision);
  if (formData.price.min) params.set("minPrice", String(formData.price.min));
  if (formData.price.max) params.set("maxPrice", String(formData.price.max));
  if (formData.beds > 0) params.set("beds", String(formData.beds));
  if (formData.maxBeds && formData.maxBeds > 0) params.set("maxBeds", String(formData.maxBeds));
  if (formData.baths > 0) params.set("baths", String(formData.baths));
  if (formData.maxBaths && formData.maxBaths > 0) params.set("maxBaths", String(formData.maxBaths));
  if (formData.sqft.min) params.set("minSqft", String(formData.sqft.min));
  if (formData.sqft.max) params.set("maxSqft", String(formData.sqft.max));
  if (formData.acres.min) params.set("minAcres", String(formData.acres.min));
  if (formData.acres.max) params.set("maxAcres", String(formData.acres.max));
  if (formData.yearBuilt.min && formData.yearBuilt.min > 1900)
    params.set("minYearBuilt", String(formData.yearBuilt.min));
  if (formData.yearBuilt.max && formData.yearBuilt.max < new Date().getFullYear())
    params.set("maxYearBuilt", String(formData.yearBuilt.max));
  if (formData.propertyTypes.length > 0)
    params.set("propertyTypes", mapPropertyTypes(formData.propertyTypes));
  if (formData.propertyFeatures.length > 0)
    params.set("propertyFeatures", formData.propertyFeatures.join(","));
  if (formData.sort) params.set("sort", formData.sort);
  if (formData.maxDaysOnMarket && formData.maxDaysOnMarket > 0)
    params.set("maxDaysOnMarket", String(formData.maxDaysOnMarket));
  // Phrases may contain commas, so keywords are pipe-delimited.
  if (formData.keywords.length > 0) params.set("keywords", formData.keywords.join("|"));

  return params;
}

/**
 * The filter part of a search: what Search and Pins send, minus the sort.
 * Two form states with the same string return the same listings.
 */
export function filterQueryString(formData: SearchFormData): string {
  const params = buildSearchParams(formData);
  params.delete("sort");
  return params.toString();
}

function applyBounds(params: URLSearchParams, bounds?: MapBounds): void {
  if (!bounds) return;
  params.set("north", String(bounds.north));
  params.set("south", String(bounds.south));
  params.set("east", String(bounds.east));
  params.set("west", String(bounds.west));
}

/**
 * Structured filter search: one page of listings for the list panel.
 * Optionally accepts map viewport bounds to limit results to the visible area.
 */
export async function searchProperties(
  formData: SearchFormData,
  limit = 24,
  offset = 0,
  bounds?: MapBounds,
  includeTotal = true,
  signal?: AbortSignal
): Promise<SearchResponse> {
  const params = buildSearchParams(formData);
  applyBounds(params, bounds);

  params.set("limit", String(limit));
  params.set("offset", String(offset));
  // Infinite-scroll pages skip the server COUNT round-trip.
  if (!includeTotal) params.set("includeTotal", "false");

  const res = await fetch(`${API_BASE}/Search?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

/**
 * Every listing matching the same filters, reduced to map pins. Fetched
 * alongside page 0 so the map always shows the full result set while the
 * list pages through it.
 */
export async function fetchPins(
  formData: SearchFormData,
  bounds?: MapBounds,
  signal?: AbortSignal
): Promise<PinsResponse> {
  const params = buildSearchParams(formData);
  applyBounds(params, bounds);
  params.delete("sort");

  const res = await fetch(`${API_BASE}/Pins?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`Pins failed: ${res.status}`);
  return res.json();
}

/** One listing in the Search result shape, for a pin popup whose listing isn't loaded in the list. */
export async function fetchListing(
  id: string,
  signal?: AbortSignal
): Promise<PropertyResult | null> {
  const res = await fetch(`${API_BASE}/Listing?id=${encodeURIComponent(id)}`, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Listing failed: ${res.status}`);
  return res.json();
}

const LISTINGS_API_BASE = "/umbraco/api/ListingsApi";

/** Outcome of a Save Search attempt, so the UI can react without parsing HTTP. */
export type SaveSearchResult =
  | { status: "saved"; id: number }
  | { status: "unauthenticated" }
  | { status: "error" };

/**
 * Saved search email alert frequencies. These are the exact codes stored in
 * SavedSearch.UpdateFrequency (SavedSearchFrequency on the server, the same
 * strings the old site saved), and they double as the labels.
 */
export const ALERT_FREQUENCIES = ["Daily", "Weekly", "Monthly", "Never"] as const;
export type AlertFrequency = (typeof ALERT_FREQUENCIES)[number];

/**
 * Persist the current filter set as a saved search against the signed-in member.
 * Sends the same structured fields buildSearchParams produces, plus a title and
 * the alert frequency. Returns "unauthenticated" on 401 so the caller can
 * redirect to login.
 */
export async function saveSearch(
  title: string,
  formData: SearchFormData,
  frequency: AlertFrequency
): Promise<SaveSearchResult> {
  const payload: Record<string, unknown> = { title, frequency };

  if (formData.location.length > 0) payload.location = formData.location.join(";");
  if (formData.address) payload.address = formData.address;
  if (formData.subdivision) payload.subdivision = formData.subdivision;
  if (formData.price.min) payload.minPrice = formData.price.min;
  if (formData.price.max) payload.maxPrice = formData.price.max;
  if (formData.beds > 0) payload.beds = formData.beds;
  if (formData.baths > 0) payload.baths = formData.baths;
  if (formData.sqft.min) payload.minSqft = formData.sqft.min;
  if (formData.sqft.max) payload.maxSqft = formData.sqft.max;
  if (formData.acres.min) payload.minAcres = formData.acres.min;
  if (formData.acres.max) payload.maxAcres = formData.acres.max;
  if (formData.yearBuilt.min && formData.yearBuilt.min > 1900)
    payload.minYearBuilt = formData.yearBuilt.min;
  if (formData.yearBuilt.max && formData.yearBuilt.max < new Date().getFullYear())
    payload.maxYearBuilt = formData.yearBuilt.max;
  if (formData.propertyTypes.length > 0) payload.propertyTypes = formData.propertyTypes.join(",");
  if (formData.propertyFeatures.length > 0)
    payload.propertyFeatures = formData.propertyFeatures.join(",");
  if (formData.sort) payload.sort = formData.sort;

  try {
    const res = await fetch(`${LISTINGS_API_BASE}/SaveSearch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });

    if (res.status === 401) return { status: "unauthenticated" };
    if (!res.ok) return { status: "error" };

    const data = await res.json().catch(() => ({}));
    return { status: "saved", id: typeof data?.id === "number" ? data.id : 0 };
  } catch {
    return { status: "error" };
  }
}

/** Outcome of an add/remove favorite attempt, so the UI can react without parsing HTTP. */
export type FavoriteResult = { status: "ok" } | { status: "unauthenticated" } | { status: "error" };

/**
 * Add a listing to the signed-in member's favorites. Mirrors saveSearch:
 * returns "unauthenticated" on 401 so the caller can redirect to login.
 * The listingId must be the MLS ListingId (PropertyResult.id), which is what
 * the Favorite table, the listing detail page and the account page all key on.
 */
export async function addFavorite(listingId: string): Promise<FavoriteResult> {
  return postFavorite(`${LISTINGS_API_BASE}/AddFavorite`, listingId);
}

/** Remove a listing from the signed-in member's favorites. */
export async function removeFavorite(listingId: string): Promise<FavoriteResult> {
  return postFavorite(`${LISTINGS_API_BASE}/RemoveFavorite`, listingId);
}

async function postFavorite(url: string, listingId: string): Promise<FavoriteResult> {
  if (!listingId) return { status: "error" };
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ listingId }),
    });

    if (res.status === 401) return { status: "unauthenticated" };
    // RemoveFavorite returns 404 when the row was already gone: treat as success.
    if (res.ok || res.status === 404) return { status: "ok" };
    return { status: "error" };
  } catch {
    return { status: "error" };
  }
}

/** A single property hit from the Autocomplete endpoint. price, beds and baths may be null. */
export interface SuggestProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number | null;
  image: string;
  propertyType: string;
  unparsedAddress: string;
  beds: number | null;
  baths: number | null;
  url: string;
}

/** Grouped autocomplete suggestions: matching properties, cities and subdivisions. */
export interface SuggestResponse {
  properties: SuggestProperty[];
  cities: string[];
  subdivisions: string[];
}

/**
 * Fetch type-ahead suggestions for the search box. Forwards an optional abort
 * signal so the caller can cancel a stale request when the query changes.
 */
export async function fetchSuggestions(q: string, signal?: AbortSignal): Promise<SuggestResponse> {
  const res = await fetch(`${LISTINGS_API_BASE}/Autocomplete?q=${encodeURIComponent(q)}`, {
    credentials: "same-origin",
    signal,
  });
  if (!res.ok) throw new Error(`Autocomplete failed: ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
//  Named lists. Separate from favorites: a listing can be in many lists at
//  once. The modal reads membership via GetListsForListing, then toggles each
//  list with AddToList / RemoveFromList, and can create a new list inline.
// ---------------------------------------------------------------------------

/** One of the member's lists plus whether the current listing is in it. */
export interface ListMembership {
  id: number;
  name: string;
  inList: boolean;
}

export type ListsResult =
  | { status: "ok"; lists: ListMembership[] }
  | { status: "unauthenticated" }
  | { status: "error" };

export type ListActionResult =
  | { status: "ok" }
  | { status: "unauthenticated" }
  | { status: "error" };

export type CreateListResult =
  | { status: "ok"; id: number; name: string }
  | { status: "unauthenticated" }
  | { status: "error" };

/** The member's lists, each flagged with whether this listing is already in it. */
export async function getListsForListing(listingId: string): Promise<ListsResult> {
  if (!listingId) return { status: "error" };
  try {
    const res = await fetch(
      `${LISTINGS_API_BASE}/GetListsForListing?listingId=${encodeURIComponent(listingId)}`,
      { credentials: "same-origin" }
    );
    if (res.status === 401) return { status: "unauthenticated" };
    if (!res.ok) return { status: "error" };

    const data = await res.json().catch(() => []);
    const lists: ListMembership[] = Array.isArray(data)
      ? data.map((d: any) => ({
          id: Number(d.id),
          name: String(d.name ?? ""),
          inList: d.inList === true,
        }))
      : [];
    return { status: "ok", lists };
  } catch {
    return { status: "error" };
  }
}

async function postList(path: string, body: Record<string, unknown>): Promise<Response | null> {
  try {
    return await fetch(`${LISTINGS_API_BASE}/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return null;
  }
}

/** Add a listing to one of the member's lists. */
export async function addToList(listId: number, listingId: string): Promise<ListActionResult> {
  const res = await postList("AddToList", { listId, listingId });
  if (!res) return { status: "error" };
  if (res.status === 401) return { status: "unauthenticated" };
  if (res.ok) return { status: "ok" };
  return { status: "error" };
}

/** Remove a listing from one of the member's lists (404 = already gone = ok). */
export async function removeFromList(listId: number, listingId: string): Promise<ListActionResult> {
  const res = await postList("RemoveFromList", { listId, listingId });
  if (!res) return { status: "error" };
  if (res.status === 401) return { status: "unauthenticated" };
  if (res.ok || res.status === 404) return { status: "ok" };
  return { status: "error" };
}

/** Create a new list, optionally dropping a listing into it on creation. */
export async function createList(name: string, listingId?: string): Promise<CreateListResult> {
  const trimmed = name.trim();
  if (!trimmed) return { status: "error" };
  const res = await postList("CreateList", { name: trimmed, listingId: listingId ?? null });
  if (!res) return { status: "error" };
  if (res.status === 401) return { status: "unauthenticated" };
  if (!res.ok) return { status: "error" };
  const data = await res.json().catch(() => ({}));
  return {
    status: "ok",
    id: typeof data?.id === "number" ? data.id : 0,
    name: String(data?.name ?? trimmed),
  };
}

/**
 * Build the public share URL: current page + the same filter params the
 * Search endpoint accepts. parseSearchParamsFromUrl reverses this so a
 * recipient opening the link lands on the identical filter set.
 */
export function buildShareUrl(formData: SearchFormData): string {
  const params = buildSearchParams(formData);
  const base = `${window.location.origin}${window.location.pathname}`;
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Read share-URL params back into a Partial<SearchFormData>. Unknown enum
 * values are silently dropped so a hand-edited URL can't poison the store.
 */
export function parseSearchParamsFromUrl(params: URLSearchParams): Partial<SearchFormData> {
  const out: Partial<SearchFormData> = {};

  const location = params.get("location");
  if (location) out.location = cleanLocations(location);

  const address = params.get("address");
  if (address) out.address = address;
  // A stale link can carry a feed stand-in ("None"). It narrows nothing real,
  // so it is neither sent nor shown as a chip.
  const subdivision = params.get("subdivision");
  if (subdivision && !isPlaceholderSubdivision(subdivision)) out.subdivision = subdivision;

  const beds = readInt(params.get("beds"));
  if (beds != null) out.beds = beds;
  const maxBeds = readInt(params.get("maxBeds"));
  if (maxBeds != null && maxBeds > 0) out.maxBeds = maxBeds;
  const baths = readInt(params.get("baths"));
  if (baths != null) out.baths = baths;
  const maxBaths = readInt(params.get("maxBaths"));
  if (maxBaths != null && maxBaths > 0) out.maxBaths = maxBaths;

  const price = readRange(params.get("minPrice"), params.get("maxPrice"), parseInt);
  if (price) out.price = price;
  const sqft = readRange(params.get("minSqft"), params.get("maxSqft"), parseInt);
  if (sqft) out.sqft = sqft;
  const acres = readRange(params.get("minAcres"), params.get("maxAcres"), parseFloat);
  if (acres) out.acres = acres;
  const yearBuilt = readRange(params.get("minYearBuilt"), params.get("maxYearBuilt"), parseInt);
  if (yearBuilt) out.yearBuilt = yearBuilt;

  const propertyTypes = params.get("propertyTypes");
  if (propertyTypes) {
    const allowed = Object.values(PropertyType) as string[];
    const matched = propertyTypes
      .split(",")
      .map((s) => s.trim())
      .filter((s) => allowed.includes(s)) as PropertyType[];
    if (matched.length > 0) out.propertyTypes = matched;
  }

  const propertyFeatures = params.get("propertyFeatures");
  if (propertyFeatures) {
    const allowed = Object.values(PropertyFeature) as string[];
    const matched = propertyFeatures
      .split(",")
      .map((s) => s.trim())
      .filter((s) => allowed.includes(s)) as PropertyFeature[];
    if (matched.length > 0) out.propertyFeatures = matched;
  }

  const sort = params.get("sort");
  if (sort && (Object.values(Sort) as string[]).includes(sort)) {
    out.sort = sort as Sort;
  }

  const maxDom = readInt(params.get("maxDaysOnMarket"));
  if (maxDom != null && maxDom > 0) out.maxDaysOnMarket = maxDom;

  const keywords = params.get("keywords");
  if (keywords) {
    const list = keywords
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .slice(0, 5);
    if (list.length > 0) out.keywords = list;
  }

  return out;
}

function readInt(v: string | null): number | null {
  if (v == null) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

function readRange(
  minStr: string | null,
  maxStr: string | null,
  parser: (s: string) => number
): Range | null {
  if (minStr == null && maxStr == null) return null;
  const out: Range = {};
  if (minStr != null) {
    const n = parser(minStr);
    if (Number.isFinite(n)) out.min = n;
  }
  if (maxStr != null) {
    const n = parser(maxStr);
    if (Number.isFinite(n)) out.max = n;
  }
  return out.min == null && out.max == null ? null : out;
}

/**
 * Parse a natural-language query into structured filters (Gemini, server-side).
 * No results are returned: the caller applies the filters and runs the normal
 * structured search, so AI queries get pagination, map-bounds and sort too.
 */
export async function parseQuery(query: string): Promise<ParseQueryResponse> {
  const params = new URLSearchParams({ q: query });
  const res = await fetch(`${API_BASE}/ParseQuery?${params.toString()}`);
  // 429 returns a structured body with rateLimited:true, so treat it as a result.
  if (res.status === 429) return res.json();
  if (!res.ok) throw new Error(`Parse failed: ${res.status}`);
  return res.json();
}
